// 2026-02 · Vista unificada del perfil de una clienta en el panel de formularios.
// Muestra historial de peso (según regla temporal), el formulario inicial y todas
// las actualizaciones en orden cronológico ASC, con opción de editar cada uno.
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FaTimes, FaSave, FaEdit, FaWeight, FaCalendarAlt, FaSyncAlt, FaFire } from 'react-icons/fa';
import FoodAutocompleteField from '@/components/FoodAutocompleteField';

const API = process.env.REACT_APP_BACKEND_URL;

// 2026-02 · Detectar campos de alimentos para activar autocompletar desde el catálogo.
const FOOD_FIELD_KEYS = new Set([
  'excluded_foods', 'disliked_foods', 'foods_to_remove', 'foods_to_add',
  'food_allergies', 'new_allergies', 'allergies',
]);

// 2026-02 · Catálogos de opciones legibles para los perfiles anidados
// (metabolic_profile / training_profile). Se mantienen idénticos al
// formulario original de Fit Evolution (MacroCalculator.jsx) para que la
// admin edite exactamente lo mismo que vio la clienta.
const METABOLIC_OPTIONS = {
  metabolism: [
    { v: 'rapido', l: '🔥 Rápido' },
    { v: 'normal', l: '⚖️ Normal' },
    { v: 'lento', l: '🐢 Lento' },
  ],
  hunger_level: [
    { v: 'alto', l: '🍽️ Alto' },
    { v: 'moderado', l: '👌 Moderado' },
    { v: 'bajo', l: '🤏 Bajo' },
  ],
  water_retention: [
    { v: 'si', l: '💧 Sí, retengo líquidos' },
    { v: 'no', l: '✨ No' },
  ],
  sleep_quality: [
    { v: 'buena', l: '😌 Buena' },
    { v: 'regular', l: '😐 Regular' },
    { v: 'mala', l: '😩 Mala' },
  ],
  goal: [
    { v: 'perder_grasa', l: '🔥 Perder grasa' },
    { v: 'ganar_musculo', l: '💪 Ganar masa muscular' },
    { v: 'mantener', l: '🌿 Mantener peso' },
    { v: 'recomposicion', l: '🔄 Recomposición corporal' },
  ],
  recommended_diet: [
    { v: 'equilibrada', l: 'Equilibrada (30P / 40C / 30G)' },
    { v: 'alta_proteina', l: 'Alta en Proteína (40P / 35C / 25G)' },
    { v: 'alta_carbos', l: 'Alta en Carbohidratos (25P / 50C / 25G)' },
    { v: 'baja_carbos', l: 'Baja en Carbohidratos (35P / 25C / 40G)' },
    { v: 'alta_grasa', l: 'Alta en Grasas Saludables (30P / 30C / 40G)' },
    { v: 'cetogenica', l: 'Cetogénica (30P / 10C / 60G)' },
  ],
};

const METABOLIC_LABELS = {
  metabolism: 'Metabolismo',
  hunger_level: 'Nivel de hambre',
  water_retention: '¿Retiene líquidos?',
  sleep_quality: 'Calidad del sueño',
  goal: 'Objetivo',
  recommended_diet: 'Dieta recomendada',
  recommended_diet_label: 'Dieta (etiqueta)',
};

const TRAINING_OPTIONS = {
  level: [
    { v: 'novato', l: '🌱 Novato (nunca o poco tiempo)' },
    { v: 'intermedio', l: '💪 Intermedio (6+ meses)' },
    { v: 'avanzado', l: '🔥 Avanzado (2+ años)' },
  ],
  training_days: [
    { v: '3', l: '3 días' },
    { v: '4', l: '4 días' },
    { v: '5', l: '5 días' },
    { v: '6', l: '6 días' },
  ],
  training_location: [
    { v: 'casa', l: '🏠 En Casa' },
    { v: 'gym', l: '🏋️ En el Gym' },
    { v: 'ambos', l: '🔀 Ambos (Casa + Gym)' },
  ],
  session_duration: [
    { v: '45min', l: '45 min' },
    { v: '60min', l: '60 min' },
    { v: '75min', l: '75 min' },
    { v: '90min', l: '90 min' },
  ],
  training_experience: [
    { v: 'novato', l: '🌱 Novato' },
    { v: 'intermedio', l: '💪 Intermedio' },
    { v: 'avanzado', l: '🔥 Avanzado' },
  ],
  goal: [
    { v: 'perder_grasa', l: '🔥 Perder grasa' },
    { v: 'ganar_musculo', l: '💪 Ganar masa muscular' },
    { v: 'mantener', l: '🌿 Mantener peso' },
    { v: 'recomposicion', l: '🔄 Recomposición corporal' },
  ],
};

const TRAINING_LABELS = {
  level: 'Nivel de entrenamiento',
  training_days: 'Días por semana',
  training_location: '¿Dónde entrena?',
  session_duration: 'Duración de la sesión',
  problematic_exercises: 'Ejercicios con molestia',
  available_equipment: 'Implementos / máquinas disponibles (casa + gym)',
  training_experience: 'Experiencia',
  goal: 'Objetivo de entrenamiento',
};

// Renderiza un <select> clásico (sin dependencia de shadcn para evitar
// portales dentro del modal) con la lista `opts`.
function OptionSelect({ value, onChange, opts, placeholder = 'Seleccionar…', testId }) {
  return (
    <select
      className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
    >
      <option value="">{placeholder}</option>
      {opts.map(o => (
        <option key={o.v} value={o.v}>{o.l}</option>
      ))}
    </select>
  );
}

// Editor amigable para `metabolic_profile`: cada subcampo se renderiza
// como dropdown o input corto, nunca como JSON crudo.
function MetabolicProfileEditor({ value, onChange }) {
  const mp = value && typeof value === 'object' ? value : {};
  const patch = (k, v) => onChange({ ...mp, [k]: v });
  const keys = ['metabolism', 'hunger_level', 'water_retention', 'sleep_quality', 'goal', 'recommended_diet'];
  return (
    <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 space-y-3" data-testid="metabolic-profile-editor">
      <p className="text-[11px] text-purple-200 uppercase tracking-wider font-semibold">Perfil metabólico</p>
      {keys.map(k => (
        <div key={k} className="space-y-1">
          <label className="text-xs text-gray-300">{METABOLIC_LABELS[k]}</label>
          <OptionSelect
            value={mp[k]}
            onChange={(v) => patch(k, v)}
            opts={METABOLIC_OPTIONS[k]}
            testId={`metabolic-${k}`}
          />
        </div>
      ))}
      {/* Otros campos no estandarizados → inputs libres para no perder datos */}
      {Object.keys(mp).filter(k => !keys.includes(k) && k !== 'recommended_diet_label').map(k => (
        <div key={k} className="space-y-1">
          <label className="text-xs text-gray-300">{k}</label>
          <input
            type="text"
            className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white"
            value={typeof mp[k] === 'object' ? JSON.stringify(mp[k]) : (mp[k] ?? '')}
            onChange={(e) => patch(k, e.target.value)}
            data-testid={`metabolic-extra-${k}`}
          />
        </div>
      ))}
    </div>
  );
}

// Editor amigable para `training_profile`: incluye el multi-select de
// equipamiento (solo visible si training_location === 'casa', igual que
// en el formulario de la clienta).
function TrainingProfileEditor({ value, onChange, equipmentCatalog }) {
  const tp = value && typeof value === 'object' ? value : {};
  const patch = (k, v) => onChange({ ...tp, [k]: v });
  const dropdownKeys = ['level', 'training_days', 'training_location', 'session_duration', 'training_experience', 'goal'];
  const equipList = (tp.available_equipment || []);
  const catalog = equipmentCatalog && equipmentCatalog.length > 0 ? equipmentCatalog : [
    { id: 'peso_corporal', label: 'Peso corporal', icon: '🤸' },
    { id: 'mancuernas', label: 'Mancuernas', icon: '🏋️' },
    { id: 'barra', label: 'Barra', icon: '➖' },
    { id: 'banda_elastica', label: 'Banda elástica', icon: '🪢' },
    { id: 'kettlebell', label: 'Kettlebell', icon: '🔔' },
    { id: 'silla_banco', label: 'Silla o banco', icon: '🪑' },
    { id: 'poleas', label: 'Polea / Cable', icon: '🎣' },
    { id: 'prensa', label: 'Prensa de piernas', icon: '🦵' },
    { id: 'maquina_flexor', label: 'Flexor sentado / acostado', icon: '🛏️' },
    { id: 'maquina_extensor', label: 'Extensor de cuádriceps', icon: '💺' },
    { id: 'abductores', label: 'Abductor / Aductor', icon: '📐' },
    { id: 'smith', label: 'Smith / Multipower', icon: '🏗️' },
  ];
  const toggleEq = (id) => {
    const cur = (tp.available_equipment || []);
    if (id === 'nada') {
      patch('available_equipment', cur.includes('nada') ? [] : ['nada']);
      return;
    }
    const withoutNada = cur.filter(x => x !== 'nada');
    patch('available_equipment', withoutNada.includes(id)
      ? withoutNada.filter(x => x !== id)
      : [...withoutNada, id]);
  };
  // 2026-02 · Ahora SIEMPRE visible: casa, gym y mixto. La admin debe poder
  // marcar o desmarcar máquinas específicas (prensa, flexor sentado, etc.)
  // sin importar dónde entrene la clienta.
  const showEquipment = true;
  return (
    <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3 space-y-3" data-testid="training-profile-editor">
      <p className="text-[11px] text-cyan-200 uppercase tracking-wider font-semibold">Perfil de entrenamiento</p>
      {dropdownKeys.map(k => (
        <div key={k} className="space-y-1">
          <label className="text-xs text-gray-300">{TRAINING_LABELS[k]}</label>
          <OptionSelect
            value={tp[k]}
            onChange={(v) => patch(k, v)}
            opts={TRAINING_OPTIONS[k]}
            testId={`training-${k}`}
          />
        </div>
      ))}
      <div className="space-y-1">
        <label className="text-xs text-gray-300">{TRAINING_LABELS.problematic_exercises}</label>
        <textarea
          rows={2}
          className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white"
          value={tp.problematic_exercises ?? ''}
          onChange={(e) => patch('problematic_exercises', e.target.value)}
          placeholder="Ej: sentadillas profundas, peso muerto…"
          data-testid="training-problematic_exercises"
        />
      </div>
      {showEquipment && (
        <div className="space-y-2" data-testid="training-equipment-selector">
          <label className="text-xs text-gray-300">{TRAINING_LABELS.available_equipment}</label>
          <p className="text-[10px] text-gray-400 italic">
            Marca todas las máquinas e implementos a los que tiene acceso (casa + gimnasio). Toca para seleccionar o quitar.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {catalog.map(eq => {
              const active = equipList.includes(eq.id);
              return (
                <button
                  key={eq.id}
                  type="button"
                  onClick={() => toggleEq(eq.id)}
                  data-testid={`training-equipment-${eq.id}`}
                  className={`px-2 py-2 rounded-lg border text-left transition-all ${active ? 'border-cyan-400 bg-cyan-500/20' : 'border-white/10 bg-white/5 hover:border-white/25'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{eq.icon || '🔧'}</span>
                    <span className={`text-[11px] font-semibold leading-tight ${active ? 'text-cyan-100' : 'text-white/90'}`}>
                      {eq.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          {equipList.length === 0 && (
            <p className="text-[10px] text-amber-300 italic">Sin implementos marcados: se asumirá solo peso corporal.</p>
          )}
        </div>
      )}
      {/* Otros campos no estandarizados */}
      {Object.keys(tp).filter(k => !dropdownKeys.includes(k) && !['problematic_exercises','available_equipment'].includes(k)).map(k => (
        <div key={k} className="space-y-1">
          <label className="text-xs text-gray-300">{k}</label>
          <input
            type="text"
            className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white"
            value={typeof tp[k] === 'object' ? JSON.stringify(tp[k]) : (tp[k] ?? '')}
            onChange={(e) => patch(k, e.target.value)}
            data-testid={`training-extra-${k}`}
          />
        </div>
      ))}
    </div>
  );
}

// Vista legible (solo lectura) para `selections`: hoy el admin genera las
// recetas desde la biblioteca, por lo que `selections` suele venir vacío
// o con `{"meal1-opt1-protein": "pollo-120g"}`. Mostrarlo como JSON crudo
// confunde — lo renderizamos como lista.
function SelectionsViewer({ value }) {
  const sel = value && typeof value === 'object' ? value : {};
  const entries = Object.entries(sel);
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-3" data-testid="selections-viewer">
      <p className="text-[11px] text-gray-300 uppercase tracking-wider font-semibold mb-2">Selecciones de alimentos</p>
      {entries.length === 0 ? (
        <p className="text-xs text-gray-400 italic">
          Sin selecciones manuales. Las recetas se asignan desde la biblioteca por la administración.
        </p>
      ) : (
        <ul className="space-y-1 text-xs text-gray-200">
          {entries.map(([k, v]) => (
            <li key={k} className="flex gap-2">
              <span className="text-pink-300 font-semibold">{k}:</span>
              <span className="text-gray-200">
                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Editor dinámico: renderiza un input adecuado por tipo primitivo. ──
function FieldEditor({ fieldKey, value, onChange, foodCatalog }) {
  const isBool = typeof value === 'boolean';
  const isArray = Array.isArray(value);
  const isObject = value !== null && typeof value === 'object' && !isArray;
  const isNumber = typeof value === 'number';

  // 2026-02 · Autocompletar cuando el campo es de alimentos (array o string vacío).
  const isFoodField = FOOD_FIELD_KEYS.has(fieldKey);
  if (isFoodField && (isArray || value === null || value === '' || typeof value === 'string')) {
    const arrValue = isArray ? value : (value ? String(value).split(',').map(s => s.replace(/\s+/g, ' ').trim()).filter(Boolean) : []);
    return (
      <FoodAutocompleteField
        value={arrValue}
        onChange={onChange}
        catalog={foodCatalog}
        testIdSuffix={fieldKey}
        placeholder='Escribe un alimento (ej. "claras de huevo") y presiona Enter'
      />
    );
  }

  if (isBool) {
    return (
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`edit-field-${fieldKey}`}
        />
        <span className="text-xs text-gray-300">Sí / No</span>
      </label>
    );
  }
  if (isArray) {
    return (
      <textarea
        className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white"
        rows={2}
        value={value.join(', ')}
        onChange={(e) => {
          // 2026-02 · Separar ÚNICAMENTE por coma. Los nombres con espacios
          // internos (ej. "claras de huevo", "carne de res") se preservan como
          // un solo elemento. NUNCA usar espacios como separador.
          const arr = e.target.value
            .split(',')
            .map(s => s.replace(/\s+/g, ' ').trim())
            .filter(Boolean);
          onChange(arr);
        }}
        placeholder='Separar SOLO por coma. Ej: "claras de huevo, carne de res"'
        data-testid={`edit-field-${fieldKey}`}
      />
    );
  }
  if (isObject) {
    return (
      <textarea
        className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-xs text-white font-mono"
        rows={4}
        value={JSON.stringify(value, null, 2)}
        onChange={(e) => {
          try {
            onChange(JSON.parse(e.target.value));
          } catch { /* mantener valor previo hasta que sea JSON válido */ }
        }}
        data-testid={`edit-field-${fieldKey}`}
      />
    );
  }
  if (isNumber) {
    return (
      <input
        type="number"
        className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white"
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        data-testid={`edit-field-${fieldKey}`}
      />
    );
  }
  return (
    <input
      type="text"
      className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`edit-field-${fieldKey}`}
    />
  );
}

// Dropdown de comida post-entreno (Comida 1 … Comida N) basado en el
// `meals_count` o `meals_per_day` del propio formulario. Nunca texto libre.
function PostWorkoutMealEditor({ value, onChange, mealsCount }) {
  const n = Math.max(2, Math.min(10, parseInt(mealsCount) || 5));
  const current = value === null || value === undefined ? '' : String(value);
  return (
    <select
      className="w-full bg-gray-900/60 border border-gray-700 rounded p-2 text-sm text-white"
      value={current}
      onChange={(e) => onChange(e.target.value)}
      data-testid="edit-field-post_workout_meal"
    >
      <option value="">— Sin selección —</option>
      <option value="ninguna">Ninguna / Sin post-entreno</option>
      {Array.from({ length: n }, (_, i) => i + 1).map(k => (
        <option key={k} value={String(k)}>{`Comida ${k} (comida después de entrenar)`}</option>
      ))}
    </select>
  );
}

function FormEditModal({ form, onClose, onSaved, foodCatalog, equipmentCatalog, showUsesSupplements, currentUsesSupplements }) {
  const initial = useMemo(() => {
    const base = { ...(form.form_data || form.data || {}) };
    // Pregunta "¿Usas suplementos?" del formulario inicial Fit Evolution: los
    // forms viejos no la guardaban. Se muestra siempre con el valor actual
    // del usuario (el que lee el bloque de suplementos).
    if (showUsesSupplements) {
      base.uses_supplements = typeof currentUsesSupplements === 'boolean'
        ? currentUsesSupplements
        : !!base.uses_supplements;
    }
    return base;
  }, [form, showUsesSupplements, currentUsesSupplements]);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  // 2026-02 · Orden preferido: campos clave (perfil metabólico, entrenamiento,
  // alimentación) arriba; selections al final (solo lectura). El resto en orden alfabético.
  const PRIORITY_ORDER = [
    'metabolic_profile', 'training_profile',
    'gender', 'age', 'weight', 'height', 'activity_level', 'goal',
    'meals_per_day', 'meals_count', 'post_workout_meal',
    'allergies', 'food_allergies', 'disliked_foods', 'excluded_foods',
    'macros', 'profile_data', 'uses_supplements',
    'selections',
  ];
  const allKeys = Object.keys(draft || {});
  const keys = [
    ...PRIORITY_ORDER.filter(k => allKeys.includes(k)),
    ...allKeys.filter(k => !PRIORITY_ORDER.includes(k)).sort(),
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      // Diff: sólo enviamos campos modificados para reducir riesgo.
      const updates = {};
      for (const k of Object.keys(draft)) {
        if (JSON.stringify(draft[k]) !== JSON.stringify(initial[k])) updates[k] = draft[k];
      }
      if (Object.keys(updates).length === 0) {
        toast.info('No hay cambios para guardar');
        setSaving(false);
        return;
      }
      const { data } = await axios.patch(
        `${API}/api/admin/forms/${form.id}`,
        { form_data_updates: updates }
      );
      // El backend ahora refleja a users + plan activo y regenera recetas si
      // corresponde (Fit Evolution). Mostrar feedback preciso al admin.
      const mirrored = (data?.mirrored_user_fields || []).concat(data?.mirrored_plan_fields || []);
      const didRegen = !!data?.regeneration_triggered;
      if (didRegen) {
        toast.success('Respuestas actualizadas. Las recetas del usuario se están regenerando en segundo plano.');
      } else if (mirrored.length > 0) {
        toast.success('Respuestas actualizadas y aplicadas al plan del usuario.');
      } else {
        toast.success('Respuestas actualizadas. Se aplicarán en el próximo plan.');
      }
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error('No se pudo guardar: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="bg-gray-950 border border-pink-500/40 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-testid="form-edit-modal">
        <div className="p-4 border-b border-pink-500/30 flex justify-between items-center sticky top-0 bg-gray-950 z-10">
          <div>
            <h3 className="text-lg font-bold text-white">Editar respuestas</h3>
            <p className="text-xs text-gray-400 mt-1">
              {form.form_type} · {new Date(form.created_at).toLocaleString('es-CO')}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="close-form-edit-modal">
            <FaTimes />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {keys.length === 0 && <p className="text-gray-400 text-sm">Este formulario no tiene campos editables.</p>}
          {keys.map((k) => {
            // Renderers especializados para objetos anidados (en vez de JSON crudo).
            if (k === 'metabolic_profile') {
              return (
                <div key={k} className="space-y-1">
                  <label className="text-xs font-semibold text-pink-300">{k}</label>
                  <MetabolicProfileEditor
                    value={draft[k]}
                    onChange={(nv) => setDraft(prev => ({ ...prev, [k]: nv }))}
                  />
                </div>
              );
            }
            if (k === 'training_profile') {
              return (
                <div key={k} className="space-y-1">
                  <label className="text-xs font-semibold text-pink-300">{k}</label>
                  <TrainingProfileEditor
                    value={draft[k]}
                    onChange={(nv) => setDraft(prev => ({ ...prev, [k]: nv }))}
                    equipmentCatalog={equipmentCatalog}
                  />
                </div>
              );
            }
            if (k === 'post_workout_meal') {
              return (
                <div key={k} className="space-y-1">
                  <label className="text-xs font-semibold text-pink-300">Comida post-entreno</label>
                  <PostWorkoutMealEditor
                    value={draft[k]}
                    onChange={(nv) => setDraft(prev => ({ ...prev, [k]: nv }))}
                    mealsCount={draft.meals_count || draft.meals_per_day}
                  />
                </div>
              );
            }
            if (k === 'selections') {
              return (
                <div key={k} className="space-y-1">
                  <label className="text-xs font-semibold text-pink-300">{k}</label>
                  <SelectionsViewer value={draft[k]} />
                </div>
              );
            }
            if (k === 'height') {
              // 2026-02 · Validación reactiva de estatura: error visible abajo
              // en rojo si < 100, se oculta automáticamente al alcanzar un
              // valor válido.
              const hv = draft[k];
              const hNum = parseFloat(hv);
              const hInvalid = hv !== '' && hv != null && !isNaN(hNum) && hNum < 100;
              return (
                <div key={k} className="space-y-1">
                  <label className="text-xs font-semibold text-pink-300">{k} (cm)</label>
                  <input
                    type="number"
                    value={hv ?? ''}
                    onChange={(e) => setDraft(prev => ({ ...prev, [k]: e.target.value }))}
                    aria-invalid={hInvalid}
                    className={`w-full bg-gray-900/60 border rounded p-2 text-sm text-white ${hInvalid ? 'border-red-500 focus:border-red-500' : 'border-gray-700'}`}
                    data-testid="form-edit-height"
                  />
                  {hInvalid && (
                    <p className="text-sm font-semibold text-red-500" data-testid="form-edit-height-error">
                      Ingresa tu estatura en centímetros (ejemplo: 179)
                    </p>
                  )}
                </div>
              );
            }
            return (
              <div key={k} className="space-y-1">
                <label className="text-xs font-semibold text-pink-300">
                  {k === 'uses_supplements' ? '💊 ¿Usa suplementos? (activa el bloque de suplementos)' : k}
                </label>
                <FieldEditor
                  fieldKey={k}
                  value={draft[k]}
                  onChange={(nv) => setDraft(prev => ({ ...prev, [k]: nv }))}
                  foodCatalog={foodCatalog}
                />
              </div>
            );
          })}
        </div>
        <div className="p-4 border-t border-pink-500/30 flex justify-end gap-2 sticky bottom-0 bg-gray-950">
          <Button variant="outline" onClick={onClose} data-testid="cancel-form-edit-btn">Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="save-form-edit-btn">
            {saving ? 'Guardando…' : <><FaSave className="mr-2" /> Guardar</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function WeightHistoryHeader({ history }) {
  if (!history) return null;
  const bucket = history.rule_bucket;
  const items = [
    history.initial ? { label: 'Peso inicial', ...history.initial, color: 'text-cyan-300' } : null,
    (bucket === 'ge_3_months' && history.previous)
      ? { label: 'Peso anterior', ...history.previous, color: 'text-amber-300' } : null,
    (bucket !== 'le_1_month' && history.current)
      ? { label: 'Peso actual', ...history.current, color: 'text-emerald-300' } : null,
  ].filter(Boolean);
  return (
    <div className="rounded-xl border border-pink-500/30 bg-gradient-to-r from-pink-500/5 to-purple-500/5 p-4 mb-4" data-testid="weight-history-header">
      <div className="flex items-center gap-2 mb-3">
        <FaWeight className="text-pink-400" />
        <h4 className="text-sm font-bold text-white">Historial de peso</h4>
        {history.months_since_initial != null && (
          <span className="text-xs text-gray-400 ml-2">({history.months_since_initial} meses desde el inicial)</span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {items.map((it, i) => (
          <div key={i} className="p-3 rounded-lg bg-black/40 border border-white/10" data-testid={`weight-${it.label.replace(/\s/g,'-').toLowerCase()}`}>
            <p className={`text-xs font-semibold uppercase tracking-wider ${it.color}`}>{it.label}</p>
            <p className="text-2xl font-bold text-white mt-1">{it.weight != null ? `${it.weight} kg` : '—'}</p>
            {it.at && (
              <p className="text-xs text-gray-500 mt-0.5">{new Date(it.at).toLocaleDateString('es-CO')}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const FE_UPDATE_LABELS = {
  weight: 'Peso (kg)',
  digestion: 'Digestión',
  inflammation: 'Inflamación',
  meal_feeling: 'Cómo sintió las comidas',
  adherence_level: 'Nivel de adherencia',
  meals_count: 'Número de comidas al día',
  current_training_location: 'Lugar de entrenamiento actual',
  uses_supplements: 'Usa suplementos',
  new_allergies: 'Nuevas alergias / alimentos a evitar',
  new_pathologies: 'Nuevas patologías',
  new_objective: 'Nuevo objetivo',
  objective_changed: 'Cambió de objetivo',
  new_equipment_available: 'Equipamiento disponible',
  new_training_time: 'Nuevo horario de entrenamiento',
  training_time_same: 'Mismo horario de entrenamiento',
  new_post_workout_meal: 'Nueva comida post-entreno',
  post_workout_meal_same: 'Misma comida post-entreno',
  preferences_changed: 'Cambió sus preferencias',
  preferences_notes: 'Notas sobre preferencias',
  exercises_to_remove: 'Ejercicios a quitar',
  muscle_group_priority: 'Grupo muscular prioritario',
  training_volume_feedback: 'Opinión sobre el volumen de entrenamiento',
  is_12week_cycle_update: 'Actualización de ciclo de 12 semanas',
  wb_was_dieting: 'Estuvo en dieta',
  wb_was_training: 'Estuvo entrenando',
  body_measurements: 'Medidas corporales',
  photos: 'Fotos',
  notes: 'Comentarios de la clienta',
  custom_answers: 'Respuestas adicionales',
  arm: 'Brazo', chest: 'Pecho', hip: 'Cadera', thigh: 'Muslo', waist: 'Cintura',
};
// Campos que ya se muestran en otro lado de la tarjeta o que son internos.
const FE_UPDATE_HIDDEN = new Set(['current_weight', 'id', 'user_id', 'email']);

const feHumanize = (k) => {
  const s = String(k).replace(/_/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const feIsEmpty = (v) => {
  if (v === null || v === undefined || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.values(v).every(feIsEmpty);
  return false;
};

const feFormatValue = (k, v) => {
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (k === 'photos' && Array.isArray(v)) return `${v.length} foto${v.length === 1 ? '' : 's'}`;
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : feHumanize(x))).join(', ');
  if (typeof v === 'object') {
    return Object.entries(v).filter(([, x]) => !feIsEmpty(x))
      .map(([kk, x]) => `${FE_UPDATE_LABELS[kk] || feHumanize(kk)}: ${feFormatValue(kk, x)}`).join(' · ');
  }
  if (typeof v === 'number') return String(v);
  if (k === 'notes' || k === 'preferences_notes' || k === 'training_volume_feedback') return String(v);
  return feHumanize(v);
};

const FE_MACRO_NAMES = { protein: 'Proteína', carbs: 'Carbohidratos', fats: 'Grasas' };

function FitEvolutionMacros({ adjustments, user, isLatest }) {
  const adj = adjustments || {};
  const pct = adj.new_macros || null;
  const grams = isLatest ? (user?.fit_evolution_target_macros_grams || null) : null;
  const kcal = adj.new_target_calories;
  const kcalNow = isLatest ? user?.fit_evolution_target_calories : null;
  const recs = Array.isArray(adj.recommendations) ? adj.recommendations : [];
  if (!pct && !grams && kcal == null && kcalNow == null && !adj.reason) return null;
  return (
    <div className="mt-3 p-2 rounded border border-amber-500/30 bg-amber-500/5 text-xs space-y-1" data-testid="fe-update-macros">
      <p className="font-semibold text-amber-300">🔥 Calorías y macros</p>
      {kcal != null && <p className="text-gray-200">Calorías objetivo tras esta actualización: <b>{kcal} kcal</b></p>}
      {pct && (
        <p className="text-gray-200">
          Distribución: {['protein', 'carbs', 'fats'].filter((m) => pct[m] != null).map((m) => `${FE_MACRO_NAMES[m]} ${pct[m]}%`).join(' · ')}
        </p>
      )}
      {(kcalNow != null || (grams && Object.keys(grams).length > 0)) && (
        <p className="text-gray-200">
          Plan actual de la clienta:{' '}
          {kcalNow != null && <b>{kcalNow} kcal</b>}
          {grams && ['protein', 'carbs', 'fats'].filter((m) => grams[m] != null).map((m) => ` · ${FE_MACRO_NAMES[m]} ${grams[m]} g`).join('')}
        </p>
      )}
      {adj.reason && <p className="text-gray-300">Ajuste: {adj.reason}</p>}
      {recs.length > 0 && (
        <ul className="list-disc pl-4 text-gray-300">{recs.map((r, i) => <li key={i}>{r}</li>)}</ul>
      )}
    </div>
  );
}

function FormTimelineItem({ form, index, isInitial, onEdit, user, isLatest }) {
  const fd = form.form_data || form.data || {};
  const isFE = !!form.read_only;
  const displayKeys = isFE
    ? Object.keys(fd).filter((k) => !FE_UPDATE_HIDDEN.has(k) && !feIsEmpty(fd[k]))
    : Object.keys(fd).slice(0, 8);
  return (
    <div className="relative pl-6 pb-4" data-testid={`timeline-item-${index}`}>
      <div className={`absolute left-0 top-1 w-3 h-3 rounded-full ${isInitial ? 'bg-cyan-400' : 'bg-emerald-400'}`}></div>
      <div className="absolute left-[5px] top-4 bottom-0 w-[2px] bg-white/10"></div>
      <div className={`p-3 rounded-lg border ${isInitial ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-emerald-500/30 bg-emerald-500/5'}`}>
        <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
          <div>
            <p className="text-xs font-bold text-white flex items-center gap-2">
              <FaCalendarAlt className="text-gray-400" />
              {isInitial ? '📋 Formulario inicial' : (isFE ? '🔄 Actualización mensual' : '🔄 Actualización')}
              <span className="text-gray-400 font-normal">
                · {isFE ? '' : `${form.form_type} · `}{new Date(form.created_at).toLocaleDateString('es-CO')}
              </span>
            </p>
            {form.admin_edited_at && (
              <p className="text-[10px] text-amber-300 mt-1">
                ✏️ Editado por admin el {new Date(form.admin_edited_at).toLocaleString('es-CO')}
              </p>
            )}
          </div>
          {!form.read_only && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(form)}
              className="text-xs"
              data-testid={`edit-form-btn-${index}`}
            >
              <FaEdit className="mr-1" /> Editar respuestas
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
          {displayKeys.map((k) => {
            const v = fd[k];
            const label = isFE ? (FE_UPDATE_LABELS[k] || feHumanize(k)) : k;
            const display = isFE ? feFormatValue(k, v) : Array.isArray(v) ? v.join(', ') :
              (typeof v === 'object' && v !== null) ? JSON.stringify(v).slice(0, 60) + '…' :
              (v === '' || v == null) ? '—' : String(v);
            return (
              <div key={k} className="flex gap-2">
                <span className="text-pink-300 font-semibold">{label}:</span>
                <span className={isFE ? 'text-gray-200 break-words' : 'text-gray-200 truncate'} title={display}>{display}</span>
              </div>
            );
          })}
          {!isFE && Object.keys(fd).length > displayKeys.length && (
            <p className="text-gray-500 italic text-[10px] col-span-2">
              +{Object.keys(fd).length - displayKeys.length} campos adicionales (usa "Editar respuestas")
            </p>
          )}
        </div>
        {isFE && <FitEvolutionMacros adjustments={form.adjustments} user={user} isLatest={isLatest} />}
      </div>
    </div>
  );
}

function CalorieAdjustmentsTimeline({ items }) {
  if (!items || items.length === 0) return null;
  const sourceLabel = {
    fit_evolution_update: '📋 Actualización Fit Evolution',
    monthly_renewal: '🔁 Renovación mensual',
    diet_continuation: '🥗 Continuación de dieta',
  };
  return (
    <div className="rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5 p-4 mb-4" data-testid="calorie-adjustments-timeline">
      <div className="flex items-center gap-2 mb-3">
        <FaFire className="text-amber-400" />
        <h4 className="text-sm font-bold text-white">Historial de ajustes calóricos ({items.length})</h4>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => {
          const delta = it.calorie_delta_pct || 0;
          const isPositive = delta > 0;
          const isZero = delta === 0;
          const colorClass = isZero ? 'text-gray-300' : isPositive ? 'text-emerald-300' : 'text-rose-300';
          const bgClass = isZero ? 'bg-gray-500/10 border-gray-500/30' : isPositive ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30';
          return (
            <div key={i} className={`p-3 rounded-lg border ${bgClass}`} data-testid={`adjustment-item-${i}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
                    {sourceLabel[it.source] || it.source} · {it.at ? new Date(it.at).toLocaleDateString('es-CO') : '—'}
                    {it.weight != null && <span className="ml-2">⚖️ {it.weight}kg</span>}
                  </p>
                  <p className="text-sm text-white mt-1">{it.reason || 'Sin razón registrada'}</p>
                  {(it.recommendations || []).filter(Boolean).map((r, ri) => (
                    <p key={ri} className="text-xs text-gray-300 italic mt-1">💡 {r}</p>
                  ))}
                </div>
                <span className={`text-2xl font-bold ${colorClass}`} data-testid={`adjustment-delta-${i}`}>
                  {isZero ? '—' : (isPositive ? `+${delta}%` : `${delta}%`)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 2026-02 · Progresión de Entrenamiento ahora vive en
// `WorkoutProgressionSection.jsx` y se monta en el Gestor Offline
// (OfflineGenerator.jsx → pestaña Rutina), junto al resto de la info de la
// clienta. Aquí ya no se renderiza para evitar duplicación.

export default function ClientProfileModal({ email, onClose }) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [editingForm, setEditingForm] = useState(null);
  const [foodCatalog, setFoodCatalog] = useState([]);
  const [equipmentCatalog, setEquipmentCatalog] = useState([]);

  // Fetch food + equipment catalog una sola vez cuando se abre el modal.
  // - `foodCatalog`: sugerencias para campos como `excluded_foods`, `allergies`.
  // - `equipmentCatalog`: multi-select de implementos en training_profile.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/offline-generator/food-database`);
        const flat = [];
        if (data && typeof data === 'object') {
          Object.entries(data).forEach(([cat, foods]) => {
            (foods || []).forEach(f => flat.push({ name: f.name, category: cat }));
          });
        }
        if (alive) setFoodCatalog(flat);
      } catch { /* silencio: sin catálogo el editor sigue funcionando */ }
      try {
        const { data } = await axios.get(`${API}/api/fit-evolution/equipment-catalog`);
        if (alive) setEquipmentCatalog(data?.catalog || []);
      } catch { /* fallback hardcodeado dentro del editor */ }
    })();
    return () => { alive = false; };
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/client-profile/${encodeURIComponent(email)}`);
      setProfile(data);
    } catch (e) {
      toast.error('Error cargando perfil: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (email) fetchProfile(); /* eslint-disable-next-line */ }, [email]);

  const handleRecompute = async () => {
    try {
      await axios.post(`${API}/api/admin/client-profile/${encodeURIComponent(email)}/recompute-exclusions`);
      toast.success('Exclusiones acumuladas recalculadas');
    } catch (e) {
      toast.error('No se pudo recalcular: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // 2026-02 · Recalcular calorías + macros desde los datos actuales guardados
  // (peso, estatura, edad, objetivo, nivel de actividad). Útil cuando el admin
  // corrige un dato del formulario y necesita actualizar los targets.
  const [recomputingCalories, setRecomputingCalories] = useState(false);
  const handleRecomputeCalories = async () => {
    setRecomputingCalories(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/client-profile/${encodeURIComponent(email)}/recompute-calories`
      );
      toast.success(
        `Calorías recalculadas: ${data.calories} kcal · P${data.macros.protein} C${data.macros.carbs} G${data.macros.fats}`
      );
    } catch (e) {
      toast.error('No se pudo recalcular calorías: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRecomputingCalories(false);
    }
  };

  if (!email) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} data-testid="client-profile-modal">
      <div className="bg-gray-950 border border-pink-500/40 rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b border-pink-500/30 flex justify-between items-center sticky top-0 bg-gray-950 z-10">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              👤 Perfil de la clienta
            </h3>
            <p className="text-xs text-gray-400 mt-1">{email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleRecomputeCalories} disabled={recomputingCalories} className="text-xs" data-testid="recompute-calories-btn">
              <FaFire className="mr-1" /> {recomputingCalories ? 'Recalculando…' : 'Recalcular calorías'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleRecompute} className="text-xs" data-testid="recompute-exclusions-btn">
              <FaSyncAlt className="mr-1" /> Recalcular exclusiones
            </Button>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="close-client-profile">
              <FaTimes />
            </button>
          </div>
        </div>
        <div className="p-4">
          {loading && <div className="text-center py-10 text-gray-400">Cargando perfil…</div>}
          {!loading && profile && (
            <>
              <WeightHistoryHeader history={profile.weight_history} />
              <CalorieAdjustmentsTimeline items={profile.calorie_adjustments} />
              <div className="mt-4" data-testid="forms-timeline">
                {profile.initial_form && (
                  <FormTimelineItem
                    form={profile.initial_form}
                    index={0}
                    isInitial
                    onEdit={setEditingForm}
                  />
                )}
                {(profile.updates || []).map((f, i) => (
                  <FormTimelineItem
                    key={f.id || i}
                    form={f}
                    index={i + 1}
                    isInitial={false}
                    onEdit={setEditingForm}
                    user={profile.user}
                    isLatest={i === (profile.updates || []).length - 1}
                  />
                ))}
                {!profile.initial_form && (!profile.updates || profile.updates.length === 0) && (
                  <p className="text-center text-gray-400 py-6">Sin formularios registrados para esta clienta.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {editingForm && (
        <FormEditModal
          form={editingForm}
          onClose={() => setEditingForm(null)}
          onSaved={() => fetchProfile()}
          foodCatalog={foodCatalog}
          equipmentCatalog={equipmentCatalog}
          showUsesSupplements={
            !!editingForm.id && editingForm.id === profile?.initial_form?.id
            && (/basic|b[aá]sico|fitevolution/.test((profile?.user?.subscription_plan || '').toLowerCase().replace(/[\s_-]/g, ''))
              || !!(editingForm.form_data || editingForm.data || {}).is_fit_evolution
              || 'uses_supplements' in (editingForm.form_data || editingForm.data || {}))
          }
          currentUsesSupplements={profile?.user?.uses_supplements}
        />
      )}
    </div>
  );
}
